import { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTeamContext } from "../../contexts/TeamContext"
import { useTeamContentPages, useTeamContentPage } from "../../hooks/useTeamContentPages"
import { TeamSectionRenderer } from "./sections/TeamSectionRenderer"
import { HorizontalNavigation } from "./HorizontalNavigation"
import { SocialMediaFooter } from "./SocialMediaFooter"
import { PublicTopNavigation } from "./PublicTopNavigation"
import { PageStyleProvider } from "../../providers/PageStyleProvider"
import { useCurrentUser } from "../../hooks/useCurrentUser"
import { EventsCacheProvider } from "../contexts/EventsCacheContext"
import { Building, FileText, Users, Calendar, Mail, Phone, Globe, Heart, Star, Award, Briefcase, Package, Shield, Key, Lock, Zap, Target, Eye } from "lucide-react"
import type { TeamContentPageIndex } from "../../types/teamContentPage.types"

// Icon mapping for content pages
const iconMap: Record<string, React.ComponentType<any>> = {
  'file-text': FileText,
  'building': Building,
  'users': Users,
  'calendar': Calendar,
  'mail': Mail,
  'phone': Phone,
  'globe': Globe,
  'heart': Heart,
  'star': Star,
  'award': Award,
  'briefcase': Briefcase,
  'package': Package,
  'shield': Shield,
  'key': Key,
  'lock': Lock,
  'zap': Zap,
  'target': Target,
}

const getIconComponent = (iconName?: string) => {
  if (!iconName) return FileText
  return iconMap[iconName] || FileText
}

// Helper function to translate gradient direction
const getGradientDirection = (direction?: string): string => {
  switch (direction) {
    case 'to-r': return 'to right';
    case 'to-l': return 'to left';
    case 'to-b': return 'to bottom';
    case 'to-t': return 'to top';
    case 'to-br': return 'to bottom right';
    case 'to-bl': return 'to bottom left';
    case 'to-tr': return 'to top right';
    case 'to-tl': return 'to top left';
    default: return direction || 'to right';
  }
}

// Group sections for layout purposes - fullWidth sections standalone,
// non-fullWidth sections paired together when consecutive
const groupSectionsForLayout = (sections: any[]) => {
  const groups: any[][] = []
  let currentPair: any[] = []

  sections.forEach((section) => {
    if (section.sectionOption?.fullWidth) {
      // If we have items in the current pair, add them first
      if (currentPair.length > 0) {
        groups.push([...currentPair])
        currentPair = []
      }
      // Add the fullWidth section as its own group
      groups.push([section])
    } else {
      // Add to current pair
      currentPair.push(section)

      // If we have 2 items in the pair, add to groups and start a new pair
      if (currentPair.length === 2) {
        groups.push([...currentPair])
        currentPair = []
      }
    }
  })

  // Add any remaining sections in the current pair
  if (currentPair.length > 0) {
    groups.push([...currentPair])
  }

  return groups
}

interface PublicHomeProps {
  initialPageSlug?: string | null;
  previewMode?: boolean;
}

export function PublicHome({ initialPageSlug, previewMode = false }: PublicHomeProps) {
  const { teamId, teamSubdomain, teamName, homePageOptions, socialMediaLinks, teamStyle } = useTeamContext()
  const navigate = useNavigate()
  const { pageSlug: urlPageSlug } = useParams<{ pageSlug: string }>()
  const [activePageSlug, setActivePageSlug] = useState<string>("")
  const { currentUser } = useCurrentUser()

  // Set page title
  useEffect(() => {
    if (teamName) {
      document.title = `Home - ${teamName}`
    }
  }, [teamName])

  // Fetch all content pages
  const {
    data: contentPages,
    isLoading: pagesLoading,
    error: pagesError
  } = useTeamContentPages(teamId, teamSubdomain)

  // Sort pages by position and find main page
  const sortedPages = useMemo(() => {
    if (!contentPages) return []
    return [...contentPages].sort((a, b) => a.position - b.position)
  }, [contentPages])

  const mainPage = useMemo(() => {
    return sortedPages.find(page => page.slug === 'main') || sortedPages[0]
  }, [sortedPages])

  // Set initial active page based on URL or props
  useEffect(() => {
    // Priority: URL param > initial prop > main page
    if (urlPageSlug) {
      setActivePageSlug(urlPageSlug)
    } else if (initialPageSlug) {
      setActivePageSlug(initialPageSlug)
    } else if (mainPage && !activePageSlug) {
      setActivePageSlug(mainPage.slug)
    }
  }, [urlPageSlug, initialPageSlug, mainPage])

  // Handle page navigation
  const handlePageChange = (newPageSlug: string) => {
    setActivePageSlug(newPageSlug)
    // Update URL when page changes
    if (newPageSlug === mainPage?.slug) {
      // Navigate to root for main page
      navigate('/')
    } else {
      // Navigate to specific page
      navigate(`/pages/${newPageSlug}`)
    }
  }

  // Fetch the active page details
  const {
    data: activePage,
    isLoading: pageLoading
  } = useTeamContentPage(teamId, teamSubdomain, activePageSlug)

  // Loading state
  if (pagesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team content...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (pagesError || !contentPages || contentPages.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Content Not Available</h2>
          <p className="text-gray-600">Team content is currently unavailable.</p>
        </div>
      </div>
    )
  }

  // Use team's home page options for hero (constant across all pages)
  const heroOptions = homePageOptions
  const hasHero = heroOptions?.showHero && heroOptions?.heroTitle
  const navigationLayout = heroOptions?.navigationLayout || 'vertical'
  const tabAlignment = (heroOptions?.tabAlignment || 'center') as 'left' | 'center' | 'right'

  console.log('hero options:', heroOptions, 'hasHero:', hasHero);

  // Debug gradient values
  if (heroOptions?.heroBackgroundType === 'gradient') {
    console.log('Gradient debug:', {
      type: heroOptions.heroBackgroundType,
      direction: heroOptions.heroGradientDirection,
      translatedDirection: getGradientDirection(heroOptions.heroGradientDirection),
      startColor: heroOptions.heroGradientStartColor,
      endColor: heroOptions.heroGradientEndColor,
      fullGradient: `linear-gradient(${getGradientDirection(heroOptions.heroGradientDirection)}, ${heroOptions.heroGradientStartColor}, ${heroOptions.heroGradientEndColor})`
    });
  }

  console.log('social media links:', socialMediaLinks);

  return (
    <EventsCacheProvider>
      <PageStyleProvider>
        <div className="min-h-screen w-full flex flex-col">
        {/* Preview Mode Banner */}
        {previewMode && (
          <div className="bg-yellow-500 text-yellow-900 px-4 py-2 text-center text-sm font-medium">
            <span className="inline-flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Preview Mode - This page is not yet published
            </span>
          </div>
        )}

        {/* Public Top Navigation */}
        <PublicTopNavigation currentUser={currentUser || undefined} />

        {/* Main content wrapper that grows - pt-16 accounts for fixed nav height */}
        <div className="flex-grow pt-16">
        {/* Hero Section - Shows on all pages when enabled */}
        {hasHero && (
        <div className="relative text-white">
          {/* Background Image Layer - Shows when image URL exists */}
          {heroOptions.heroBackgroundImageUrl && (
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${heroOptions.heroBackgroundImageUrl})`
              }}
            />
          )}

          {/* Background Color/Gradient Layer - Shows when no image */}
          {!heroOptions.heroBackgroundImageUrl && (
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: heroOptions.heroBackgroundType === 'color'
                  ? heroOptions.heroBackgroundColor
                  : undefined,
                backgroundImage: heroOptions.heroBackgroundType === 'gradient'
                  ? `linear-gradient(${getGradientDirection(heroOptions.heroGradientDirection)}, ${heroOptions.heroGradientStartColor}, ${heroOptions.heroGradientEndColor})`
                  : undefined
              }}
            />
          )}

          {/* Overlay Layer - Works on top of both image and color/gradient backgrounds */}
          {heroOptions.overlayType && heroOptions.overlayType !== 'none' && (
            <div
              className="absolute inset-0 z-[1]"
              style={{
                backgroundColor: heroOptions.overlayType === 'color'
                  ? heroOptions.overlayColor
                  : undefined,
                backgroundImage: heroOptions.overlayType === 'gradient'
                  ? `linear-gradient(${getGradientDirection(heroOptions.overlayGradientDirection)}, ${heroOptions.overlayGradientStart}, ${heroOptions.overlayGradientEnd})`
                  : undefined,
                opacity: heroOptions.heroOverlayOpacity || 0.9
              }}
            />
          )}

          {/* Content Layer */}
          <div className="relative z-10 container mx-auto px-4 py-16">
            <div className="max-w-3xl mx-auto text-center">
              {heroOptions.heroDetailsBackgroundCard ? (
                <div
                  className="p-8 rounded-lg"
                  style={{
                    backgroundColor: heroOptions.heroDetailsCardBackgroundColor || '#000000',
                    opacity: heroOptions.heroDetailsCardOpacity || 0.7
                  }}
                >
                  <h1 className="text-4xl font-bold mb-4" style={{ color: heroOptions.heroTextColor }}>
                    {heroOptions.heroTitle}
                  </h1>
                  {heroOptions.heroSubtitle && (
                    <p className="text-xl mb-8" style={{ color: heroOptions.heroTextColor + 'CC' }}>
                      {heroOptions.heroSubtitle}
                    </p>
                  )}
                  {heroOptions.heroCtaEnabled && (
                    <div className="flex gap-4 flex-wrap justify-center">
                      {heroOptions.heroCtaText && heroOptions.heroCtaLink && (
                        <a
                          href={heroOptions.heroCtaLink}
                          className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors"
                          style={{
                            backgroundColor: heroOptions.heroButtonBgColor,
                            color: heroOptions.heroButtonTextColor
                          }}
                        >
                          {heroOptions.heroCtaText}
                        </a>
                      )}
                      {heroOptions.showHeroSecondaryCta && heroOptions.heroSecondaryCtaText && heroOptions.heroSecondaryCtaLink && (
                        <a
                          href={heroOptions.heroSecondaryCtaLink}
                          className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors border-2"
                          style={{
                            backgroundColor: heroOptions.heroSecondaryButtonBgColor || 'transparent',
                            color: heroOptions.heroSecondaryButtonTextColor,
                            borderColor: heroOptions.heroSecondaryButtonBorderColor
                          }}
                        >
                          {heroOptions.heroSecondaryCtaText}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h1 className="text-4xl font-bold mb-4" style={{ color: heroOptions.heroTextColor }}>
                    {heroOptions.heroTitle}
                  </h1>
                  {heroOptions.heroSubtitle && (
                    <p className="text-xl mb-8" style={{ color: heroOptions.heroTextColor + 'CC' }}>
                      {heroOptions.heroSubtitle}
                    </p>
                  )}
                  {heroOptions.heroCtaEnabled && (
                    <div className="flex gap-4 flex-wrap justify-center">
                      {heroOptions.heroCtaText && heroOptions.heroCtaLink && (
                        <a
                          href={heroOptions.heroCtaLink}
                          className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors"
                          style={{
                            backgroundColor: heroOptions.heroButtonBgColor,
                            color: heroOptions.heroButtonTextColor
                          }}
                        >
                          {heroOptions.heroCtaText}
                        </a>
                      )}
                      {heroOptions.showHeroSecondaryCta && heroOptions.heroSecondaryCtaText && heroOptions.heroSecondaryCtaLink && (
                        <a
                          href={heroOptions.heroSecondaryCtaLink}
                          className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors border-2"
                          style={{
                            backgroundColor: heroOptions.heroSecondaryButtonBgColor || 'transparent',
                            color: heroOptions.heroSecondaryButtonTextColor,
                            borderColor: heroOptions.heroSecondaryButtonBorderColor
                          }}
                        >
                          {heroOptions.heroSecondaryCtaText}
                        </a>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Horizontal Navigation - Shows when layout is horizontal */}
      {navigationLayout === 'horizontal' && (
        <HorizontalNavigation
          pages={sortedPages.map(page => ({
            id: page.id,
            name: page.title,
            slug: page.slug,
            icon: page.icon,
            requiresAuth: page.requireAccount
          }))}
          activePageSlug={activePageSlug}
          onPageChange={handlePageChange}
          tabAlignment={tabAlignment}
          primaryColor={teamStyle?.headerColor}
        />
      )}

      {/* Main Content */}
      {navigationLayout === 'horizontal' ? (
        // Horizontal layout - full width content below navigation
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {pageLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading content...</p>
            </div>
          ) : activePage ? (
            <div className="mt-12 space-y-8">
              {/* Render sections without title/subtitle */}
              {activePage.sections && activePage.sections.length > 0 ? (
                <>
                  {groupSectionsForLayout(activePage.sections).map((group, groupIndex) => {
                    // If this is a single fullWidth section
                    if (group.length === 1 && group[0].sectionOption?.fullWidth) {
                      return (
                        <div key={group[0].id}>
                          <TeamSectionRenderer section={group[0]} />
                        </div>
                      )
                    }
                    // If this is a pair of non-fullWidth sections or a single non-fullWidth section
                    return (
                      <div key={`group-${groupIndex}`} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {group.map(section => (
                          <div key={section.id}>
                            <TeamSectionRenderer section={section} />
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <p className="text-gray-500 text-center py-12">
                    No content available for this page.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-500 text-center py-12">
                Select a page to view content.
              </p>
            </div>
          )}
        </div>
      ) : (
        // Vertical layout - sidebar navigation
        <div className="container mx-auto px-4 py-12">
          <div className={`flex flex-col gap-8 ${tabAlignment === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
            {/* Tabs Navigation */}
            <div className="md:w-80 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="space-y-2">
                  {sortedPages.map(page => {
                    const Icon = getIconComponent(page.icon)
                    const isActive = activePageSlug === page.slug
                    const primaryColor = teamStyle?.headerColor || '#1D31FF'

                    return (
                      <button
                        key={page.id}
                        onClick={() => handlePageChange(page.slug)}
                        disabled={page.requireAccount} // Disable if authentication required
                        title={`${page.title}${page.subtitle ? ` - ${page.subtitle}` : ''}`} // Tooltip for full text
                        className={`w-full flex items-start gap-3 p-4 rounded-lg text-left transition-colors ${
                          isActive
                            ? "text-white"
                            : page.requireAccount
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "hover:bg-gray-50 text-gray-700"
                        }`}
                        style={isActive ? { backgroundColor: primaryColor } : {}}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="font-medium break-words">{page.title}</span>
                          {page.subtitle && (
                            <span className="text-sm opacity-80 break-words mt-1">{page.subtitle}</span>
                          )}
                        </div>
                        {page.requireAccount && (
                          <Lock className="h-4 w-4 ml-2 flex-shrink-0 mt-0.5" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1">
              {pageLoading ? (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading content...</p>
                  </div>
                </div>
              ) : activePage ? (
                <div className="space-y-8">
                  {/* Render sections without title/subtitle */}
                  {activePage.sections && activePage.sections.length > 0 ? (
                    <>
                      {groupSectionsForLayout(activePage.sections).map((group, groupIndex) => {
                        // If this is a single fullWidth section
                        if (group.length === 1 && group[0].sectionOption?.fullWidth) {
                          return (
                            <div key={group[0].id}>
                              <TeamSectionRenderer section={group[0]} />
                            </div>
                          )
                        }
                        // If this is a pair of non-fullWidth sections or a single non-fullWidth section
                        return (
                          <div key={`group-${groupIndex}`} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {group.map(section => (
                              <div key={section.id}>
                                <TeamSectionRenderer section={section} />
                              </div>
                            ))}
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    <div className="bg-white rounded-xl shadow-lg p-6">
                      <p className="text-gray-500 text-center py-12">
                        No content available for this page.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <p className="text-gray-500 text-center py-12">
                    Select a page to view content.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Apply custom CSS if provided */}
      {homePageOptions?.customCss && (
        <style dangerouslySetInnerHTML={{ __html: homePageOptions.customCss }} />
      )}
      {activePage?.contentPageOption?.customCss && (
        <style dangerouslySetInnerHTML={{ __html: activePage.contentPageOption.customCss }} />
      )}

      {/* Social Media Footer */}
      {socialMediaLinks && socialMediaLinks.length > 0 && (
        <SocialMediaFooter socialMediaLinks={socialMediaLinks} />
      )}
      </div>
      </PageStyleProvider>
    </EventsCacheProvider>
  )
}